import { styled } from '@linaria/react';
import { ActionIcon, Group, ScrollArea, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { AgentMinerSession } from '../../../../api/agent-miner-client/agent-miner-client.type';
import { BrandedLoadingOverlay } from '../../../../components/loader/branded-loader';
import {
  baseWhite,
  inkDarkest,
  inkLight,
  inkLighter,
  secondaryBase,
  skyDark,
  skyLight,
  skyLightest,
} from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { useShowAgentMinerApiCost } from '../agent-miner.hooks';
import { AgentSourceIcon } from '../agent-source-icon';
import { CategoryChip } from '../category-chip';
import { fmtCost, fmtNum, fmtRelative } from '../formatters';
import { getSessionDisplayTitle } from '../shared.helpers';
import { StatusDot } from '../status-dot';
import { TasksChipList } from '../tasks-chip-list';
import type { SessionListPanelProps } from './session-list-panel.type';

const dividerMargin = { margin: '16px 0' } as const;

/**
 * One clickable session summary card. Mirrors the sessions table's columns in
 * a vertical layout: title with its agent source, status and last activity,
 * then the turn/token/cost counters and any linked work units.
 *
 * @param {{ session: AgentMinerSession, isSelected: boolean, onClick: () => void }} props - Component props
 * @returns {JSX.Element} The session card
 */
const SessionListItem = ({
  session,
  isSelected,
  onClick,
}: {
  session: AgentMinerSession;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const showApiCost = useShowAgentMinerApiCost();
  const tokens = (session.total_input_tokens ?? 0) + (session.total_output_tokens ?? 0);
  const turns = session.turns_count ?? 0;

  return (
    <ItemCard type="button" onClick={onClick} data-selected={isSelected} data-testid="session-list-item">
      <Group gap={8} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
        <AgentSourceIcon source={session.source} size={16} />
        <ItemTitle>{getSessionDisplayTitle(session)}</ItemTitle>
      </Group>

      <Group gap={6} align="center" wrap="nowrap">
        <StatusDot status={session.status} size={7} />
        <Text size="small" color={inkLight}>
          {session.status}
        </Text>
        <Bullet>•</Bullet>
        <Text size="small" color={inkLighter}>
          {fmtRelative(session.last_turn_at ?? session.started_at)}
        </Text>
      </Group>

      <MetaRow>
        <MetaItem>
          <Icon name="forum" size={12} color={inkLighter} />
          {turns}
        </MetaItem>
        <MetaItem>
          <Icon name="memory" size={12} color={inkLighter} />
          {fmtNum(tokens)}
        </MetaItem>
        {showApiCost && (
          <MetaItem>
            <Icon name="payments" size={12} color={inkLighter} />
            {fmtCost(session.total_cost_usd)}
          </MetaItem>
        )}
      </MetaRow>

      {(session.flora_annotations?.label || session.linked_tasks?.length > 0) && (
        <Group gap={4} wrap="wrap">
          {session.flora_annotations?.label && <CategoryChip label={session.flora_annotations.label} size="sm" />}
          {session.linked_tasks?.length > 0 && <TasksChipList tasks={session.linked_tasks} size="sm" wrap />}
        </Group>
      )}
    </ItemCard>
  );
};

/**
 * Universal side-panel list of agent sessions, scoped by whatever the caller
 * passes in (a model family, a user, a cohort). Click a row to drill into a
 * session; the optional expand action hands off to the full Sessions view.
 *
 * Purely presentational — see {@link SessionListSideSection} for the fetching
 * container that pairs it with the session detail panel.
 *
 * @param {SessionListPanelProps} props - Component props
 * @returns {JSX.Element} The list panel
 */
export function SessionListPanel({
  title,
  subtitle,
  sessions,
  totalCount,
  isLoading,
  isError = false,
  selectedSessionId,
  onSelectSession,
  onClose,
  onExpand,
  expandLabel,
  emptyLabel,
}: SessionListPanelProps) {
  const { t } = useTranslation();
  // `totalCount` is the server-side match count; `sessions` is only the first
  // page, so the footer tells the user when there is more behind the expand.
  const hasMore = totalCount > sessions.length;

  return (
    <Wrapper>
      <BrandedLoadingOverlay visible={isLoading} variant="colored" />
      <Header>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text weight="bold" size="medium" color={secondaryBase}>
              {`${title} (${totalCount})`}
            </Text>
            {subtitle && (
              <Text size="small" color={inkLighter}>
                {subtitle}
              </Text>
            )}
          </Stack>
          <ActionIcon
            variant="subtle"
            size="sm"
            radius="xl"
            color={skyDark}
            onClick={onClose}
            aria-label={t('common.actions.close')}
          >
            <Icon name="close" size={18} color={skyDark} />
          </ActionIcon>
        </Group>

        {onExpand && (
          <Button
            fullWidth
            size="xs"
            radius="xl"
            variant="secondary"
            onClick={onExpand}
            disabled={totalCount === 0}
            leftSection={<Icon name="open_in_full" variant="filled" size={14} color={baseWhite} />}
            style={{ marginTop: 12 }}
          >
            {expandLabel ?? t('agent_miner.session_list.expand')}
          </Button>
        )}
      </Header>

      <Divider style={dividerMargin} />

      {isError ? (
        <EmptyState>
          <Icon name="error" size={32} color={inkLighter} />
          <Text size="small" color={inkLight}>
            {t('agent_miner.session_list.error')}
          </Text>
        </EmptyState>
      ) : !isLoading && sessions.length === 0 ? (
        <EmptyState>
          <Icon name="folder_open" size={32} color={inkLighter} />
          <Text size="small" color={inkLight}>
            {emptyLabel ?? t('agent_miner.session_list.empty')}
          </Text>
        </EmptyState>
      ) : (
        <ScrollArea style={{ flex: 1, minHeight: 0 }} scrollbarSize={6}>
          <Stack gap={8} pb={8}>
            {sessions.map((session) => (
              <SessionListItem
                key={session.id}
                session={session}
                isSelected={session.id === selectedSessionId}
                onClick={() => onSelectSession(session.id)}
              />
            ))}
            {hasMore && (
              <Footnote>
                {t('agent_miner.session_list.showing', {
                  shown: sessions.length,
                  total: totalCount,
                })}
              </Footnote>
            )}
          </Stack>
        </ScrollArea>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  position: relative;
`;

const Header = styled.div`
  padding-top: 4px;
  flex-shrink: 0;
`;

const ItemCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  text-align: left;
  background: ${skyLightest};
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  font-family: 'Figtree', sans-serif;

  &:hover {
    background: ${skyLight};
  }

  &[data-selected='true'] {
    border-color: ${secondaryBase};
  }
`;

const ItemTitle = styled.span`
  font: 600 13px/1.3 'Figtree', sans-serif;
  color: ${inkDarkest};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const Bullet = styled.span`
  color: ${skyDark};
  font-size: 11px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font: 500 11.5px/1 'Figtree', sans-serif;
  color: ${inkLight};
  font-variant-numeric: tabular-nums;
`;

const EmptyState = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 32px 12px;
`;

const Footnote = styled.div`
  font: 400 11px/1.4 'Figtree', sans-serif;
  color: ${inkLighter};
  text-align: center;
  padding: 4px 0;
`;
